import React, { useEffect, useMemo, useState } from 'react';
import { Users, RefreshCw, Search, X, Plus, Trash2, Tag, Mail, Phone, Activity } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Drawer, Select } from '@/components/admin/shared';

const SOURCE_TABS = [
  { key: 'all',        label: 'All' },
  { key: 'newsletter', label: 'Newsletter' },
  { key: 'purchase',   label: 'Purchases' },
  { key: 'collab',     label: 'Collab' },
  { key: 'download',   label: 'Downloads' },
  { key: 'manual',     label: 'Manual' },
];

function fmtDate(s) { if (!s) return ''; try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; } }

export default function CustomersManager() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, by_source: {} });
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', phone: '', communication_preferences: 'email', source: 'manual' });

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (tab !== 'all') params.source = tab;
      if (q.trim()) params.q = q.trim();
      const [{ data: list }, { data: c }] = await Promise.all([
        api.get('/admin/customers', { params }),
        api.get('/admin/customers/counts'),
      ]);
      setItems(list);
      setCounts(c);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [q]);

  const openDetail = async (c) => {
    try {
      const { data } = await api.get(`/admin/customers/${c.id}`);
      setDetail(data);
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to load customer'); }
  };

  const saveDetail = async () => {
    try {
      const { data } = await api.put(`/admin/customers/${detail.id}`, {
        name: detail.name || '',
        phone: detail.phone || '',
        communication_preferences: detail.communication_preferences || 'email',
        tags: detail.tags || [],
        notes: detail.notes || '',
        subscription_status: detail.subscription_status || 'none',
      });
      setDetail(data);
      setMsg('Saved.');
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
  };

  const resyncBrevo = async () => {
    setMsg('Syncing to Brevo…');
    try {
      const { data } = await api.post(`/admin/customers/${detail.id}/resync-brevo`);
      setMsg(data.ok ? 'Synced to Brevo.' : `Brevo error: ${data.error || 'unknown'}`);
      load();
      openDetail({ id: detail.id });
    } catch (e) { setMsg(e?.response?.data?.detail || 'Sync failed'); }
  };

  const removeCustomer = async () => {
    if (!window.confirm(`Delete ${detail.email}? This does NOT delete their Brevo contact.`)) return;
    try {
      await api.delete(`/admin/customers/${detail.id}`);
      setDetail(null);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Delete failed'); }
  };

  const createCustomer = async () => {
    if (!form.email.trim()) return;
    try {
      await api.post('/admin/customers', form);
      setNewOpen(false);
      setForm({ email: '', name: '', phone: '', communication_preferences: 'email', source: 'manual' });
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Create failed'); }
  };

  return (
    <div data-testid="admin-customers-manager">
      <div className="pg-admin-bar flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-[var(--pg-ink-muted)]" />
          <p className="text-sm text-[var(--pg-ink)] font-mono">
            {loading ? 'Loading…' : `${counts.total} customers`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="customers-refresh" className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1"><RefreshCw size={13} /> REFRESH</button>
          <button onClick={() => setNewOpen(true)} data-testid="customers-new" className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2"><Plus size={14} /> NEW CUSTOMER</button>
        </div>
      </div>

      {/* Search + tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] pg-input rounded-[var(--pg-radius-sm)] px-3 py-1.5">
          <Search size={13} className="text-[var(--pg-ink-faint)]" />
          <input
            data-testid="customers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or name…"
            className="bg-transparent outline-none text-sm w-full text-[var(--pg-ink)]"
          />
          {q && <button onClick={() => setQ('')} className="text-[var(--pg-ink-faint)]"><X size={13} /></button>}
        </div>
      </div>
      <div className="flex items-center flex-wrap gap-1.5 mb-3" data-testid="customers-source-tabs">
        {SOURCE_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`customers-tab-${t.key}`}
            className={`px-3 py-1.5 rounded-full border text-[10px] font-mono tracking-[0.24em] uppercase ${tab === t.key ? 'bg-[rgba(255,62,165,0.12)] border-[var(--pg-neon-pink)] text-[var(--pg-neon-pink)]' : 'border-[var(--pg-border-2)] text-[var(--pg-ink-muted)]'}`}
          >
            {t.label}
            {t.key !== 'all' && typeof counts.by_source?.[t.key] === 'number' && (
              <span className="ml-1.5 text-[9px] text-[var(--pg-ink-faint)]">{counts.by_source[t.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="pg-card overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center text-[var(--pg-ink-muted)]">
            {loading ? 'Loading…' : 'No customers match.'}
          </div>
        ) : (
          <ul className="divide-y divide-[var(--pg-border-2)]">
            {items.map((c) => (
              <li key={c.id} className="p-4 flex items-center gap-4 hover:bg-[rgba(255,255,255,0.03)] cursor-pointer" data-testid={`customer-row-${c.id}`} onClick={() => openDetail(c)}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--pg-ink)] truncate">{c.name || c.email}</p>
                  <p className="text-xs text-[var(--pg-ink-muted)] truncate">
                    {c.email}{c.phone ? ` · ${c.phone}` : ''}{c.purchase_count ? ` · ${c.purchase_count} purchase(s)` : ''}
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 flex-wrap max-w-[35%] justify-end">
                  {(c.sources || []).map((s) => (
                    <span key={s} className="text-[9px] font-mono tracking-widest uppercase px-1.5 py-0.5 rounded-full border border-[var(--pg-border-2)] text-[var(--pg-ink-faint)]">{s}</span>
                  ))}
                  {(c.tags || []).slice(0, 2).map((t) => (
                    <span key={t} className="text-[9px] font-mono tracking-widest uppercase px-1.5 py-0.5 rounded-full border border-[var(--pg-emerald)] text-[var(--pg-emerald-2)]">{t}</span>
                  ))}
                </div>
                <span className="text-[10px] font-mono text-[var(--pg-ink-faint)] w-24 text-right">{fmtDate(c.last_seen)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {msg && <p className="text-xs text-[var(--pg-tertiary)] font-mono tracking-widest mt-3">{msg}</p>}

      {/* Detail drawer */}
      <Drawer open={!!detail} onClose={() => setDetail(null)} title={detail ? (detail.name || detail.email) : ''}>
        {detail && (
          <div className="space-y-4" data-testid="customer-detail">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Email</label>
                <p className="text-sm text-[var(--pg-ink)] mt-1 flex items-center gap-2"><Mail size={13} /> {detail.email}</p>
              </div>
              <TextField label="Name" value={detail.name || ''} onChange={(v) => setDetail({ ...detail, name: v })} />
              <TextField label="Phone" value={detail.phone || ''} onChange={(v) => setDetail({ ...detail, phone: v })} placeholder="+15551234567" />
              <Select label="Communication preferences" value={detail.communication_preferences || 'email'} onChange={(v) => setDetail({ ...detail, communication_preferences: v })}
                options={[{ value: 'email', label: 'Email only' }, { value: 'sms', label: 'SMS only' }, { value: 'both', label: 'Email + SMS' }]} />
              <div>
                <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">Tags (comma-separated)</label>
                <input
                  data-testid="customer-tags-input"
                  className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm"
                  value={(detail.tags || []).join(', ')}
                  onChange={(e) => setDetail({ ...detail, tags: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                />
              </div>
              <Select label="Subscription status" value={detail.subscription_status || 'none'} onChange={(v) => setDetail({ ...detail, subscription_status: v })}
                options={[{ value: 'none', label: 'None' }, { value: 'active', label: 'Active' }, { value: 'cancelled', label: 'Cancelled' }, { value: 'past_due', label: 'Past due' }]} />
              <TextField label="Internal notes" value={detail.notes || ''} onChange={(v) => setDetail({ ...detail, notes: v })} textarea rows={3} />
            </div>

            <div className="pg-card p-3 text-xs text-[var(--pg-ink-muted)]">
              <p className="font-mono tracking-widest text-[10px] mb-2">TIMELINE</p>
              <p>First seen: {fmtDate(detail.first_seen)}</p>
              <p>Last seen: {fmtDate(detail.last_seen)}</p>
              <p>Sources: {(detail.sources || []).join(', ') || '—'}</p>
              <p>Purchases: {detail.purchase_count || 0} · Total spent: ${Number(detail.total_spent || 0).toFixed(2)}</p>
              <p>Brevo contact: {detail.brevo_contact_id ? `#${detail.brevo_contact_id} (synced ${fmtDate(detail.brevo_synced_at)})` : 'not synced'}</p>
            </div>

            {(detail.orders || []).length > 0 && (
              <div className="pg-card p-3 text-xs">
                <p className="font-mono tracking-widest text-[10px] text-[var(--pg-ink-muted)] mb-2">ORDERS ({detail.orders.length})</p>
                <ul className="divide-y divide-[var(--pg-border-2)]">
                  {detail.orders.slice(0, 8).map((o) => (
                    <li key={o.session_id || o.id} className="py-1.5 flex items-center justify-between">
                      <span className="text-[var(--pg-ink)] truncate">{o.product_title || 'Order'}</span>
                      <span className="text-[var(--pg-ink-faint)]">{o.payment_status || o.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={removeCustomer} data-testid="customer-delete" className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1 text-[var(--pg-neon-pink)]"><Trash2 size={13} /> Delete</button>
              <button onClick={resyncBrevo} data-testid="customer-brevo-sync" className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1"><Activity size={13} /> Sync to Brevo</button>
              <button onClick={saveDetail} data-testid="customer-save" className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-4 py-1.5 text-xs">Save</button>
            </div>
          </div>
        )}
      </Drawer>

      {/* New customer drawer */}
      <Drawer open={newOpen} onClose={() => setNewOpen(false)} title="New customer">
        <div className="space-y-4">
          <TextField label="Email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="jane@example.com" />
          <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <TextField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+15551234567" />
          <Select label="Communication preferences" value={form.communication_preferences} onChange={(v) => setForm({ ...form, communication_preferences: v })}
            options={[{ value: 'email', label: 'Email only' }, { value: 'sms', label: 'SMS only' }, { value: 'both', label: 'Email + SMS' }]} />
          <Select label="Source" value={form.source} onChange={(v) => setForm({ ...form, source: v })}
            options={[{ value: 'manual', label: 'Manual' }, { value: 'newsletter', label: 'Newsletter' }, { value: 'collab', label: 'Collab' }, { value: 'purchase', label: 'Purchase' }, { value: 'download', label: 'Download' }]} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setNewOpen(false)} className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs">Cancel</button>
            <button onClick={createCustomer} data-testid="customer-create-save" className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-4 py-1.5 text-xs">Create</button>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
